<template>
  <section class="histo" data-test="status-histogram">
    <header class="histo-head">
      <h3 class="histo-title">{{ title }}</h3>
      <span class="histo-total">{{ total }} total</span>
    </header>
    <ul v-if="entries.length" class="histo-list">
      <li
        v-for="entry in entries"
        :key="entry.name"
        class="histo-row"
        :class="entry.bucket"
        :data-test="`histo-row-${entry.bucket}`"
      >
        <span class="histo-label" :title="entry.name">{{ entry.name }}</span>
        <span class="histo-bar-track" :aria-label="`${entry.count} of ${total}`">
          <span class="histo-bar" :style="{ width: pctWidth(entry.count) }" />
        </span>
        <span class="histo-count mono">{{ entry.count }}</span>
      </li>
    </ul>
    <p v-else class="histo-empty">No statuses recorded.</p>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    histogram?: Record<string, number>;
    title?: string;
    bucketize?: (status: string) => "done" | "in-flight" | "blocked" | "open";
  }>(),
  { title: "Status breakdown" },
);

// Default bucketization for Foodics workflows. Used to color the bars
// without forcing the SPA to know each project's status taxonomy.
const DEFAULT_DONE = new Set([
  "Done", "Closed", "Resolved",
  "Closed Fixed", "Closed as designed",
  "Released", "Ready for Releasing", "Ready for Release",
]);
const DEFAULT_OPEN = new Set(["Open", "Backlog", "To Do", "Reopened"]);
const DEFAULT_BLOCKED_HINT = ["block", "won't", "wont"];

function defaultBucket(name: string): "done" | "in-flight" | "blocked" | "open" {
  if (DEFAULT_DONE.has(name)) return "done";
  if (DEFAULT_OPEN.has(name)) return "open";
  const lower = name.toLowerCase();
  if (DEFAULT_BLOCKED_HINT.some((h) => lower.includes(h))) return "blocked";
  return "in-flight";
}

interface Entry {
  name: string;
  count: number;
  bucket: "done" | "in-flight" | "blocked" | "open";
}

const entries = computed<Entry[]>(() => {
  const h = props.histogram ?? {};
  return Object.entries(h)
    .map(([name, count]) => ({
      name,
      count: Number(count),
      bucket: (props.bucketize ?? defaultBucket)(name),
    }))
    .sort((a, b) => b.count - a.count);
});

const total = computed(() => entries.value.reduce((s, e) => s + e.count, 0));

function pctWidth(count: number): string {
  if (total.value === 0) return "0%";
  return `${Math.round((count / total.value) * 100)}%`;
}
</script>

<style scoped>
.histo { display: flex; flex-direction: column; gap: 10px; }
.histo-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.histo-title { font-size: 14px; font-weight: 600; margin: 0; color: var(--text-color, #111827); }
.histo-total { font-size: 11px; color: var(--text-muted, #6b7280); text-transform: uppercase; letter-spacing: 0.04em; }
.histo-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.histo-row {
  display: grid;
  grid-template-columns: minmax(110px, max-content) 1fr 32px;
  align-items: center;
  gap: 8px;
}
.histo-label {
  font-size: 12px; color: var(--text-color, #111827);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.histo-bar-track {
  background: var(--surface-elev, #f3f4f6);
  border-radius: 4px;
  height: 10px;
  overflow: hidden;
  display: block;
}
.histo-bar { display: block; height: 100%; border-radius: 4px; transition: width 200ms ease-out; }
.histo-row.done .histo-bar { background: #15803d; }
.histo-row.in-flight .histo-bar { background: #2563eb; }
.histo-row.blocked .histo-bar { background: #b45309; }
.histo-row.open .histo-bar { background: #6b7280; }
.histo-count { font-size: 12px; color: var(--text-muted, #4b5563); text-align: right; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.histo-empty { font-size: 12px; color: var(--text-muted, #6b7280); margin: 0; }
</style>
